import React, { useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  CheckCircle,
  Error as ErrorIcon,
  RemoveCircleOutline,
  ExpandMore,
  PlaylistAddCheck,
  Search,
  Analytics,
  Assignment,
  Timer,
  Code,
  KeyboardArrowDown,
  KeyboardArrowUp,
  SmartToy,
  ContentCopy,
  Delete,
} from '@mui/icons-material'
import Markdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import { api } from '../../services/Main/Api'
import moment from 'moment'

const STEP_ICONS = {
  PLAN: <PlaylistAddCheck sx={{ fontSize: 18 }} />,
  TOOL_CALL: <Search sx={{ fontSize: 18 }} />,
  ANALYSIS: <Analytics sx={{ fontSize: 18 }} />,
  CONCLUSION: <Assignment sx={{ fontSize: 18 }} />,
}

const STATUS_CONFIG = {
  SUCCESS: { icon: <CheckCircle sx={{ fontSize: 16, color: '#16A34A' }} />, color: '#D1FAE5', textColor: '#065F46', label: 'OK' },
  FAILED: { icon: <ErrorIcon sx={{ fontSize: 16, color: '#DC2626' }} />, color: '#FEE2E2', textColor: '#991B1B', label: 'Falhou' },
  NO_DATA: { icon: <RemoveCircleOutline sx={{ fontSize: 16, color: '#D97706' }} />, color: '#FEF3C7', textColor: '#92400E', label: 'Sem dados' },
}

const StepDetail = ({ step }) => {
  const [showOutput, setShowOutput] = useState(false)
  const statusCfg = STATUS_CONFIG[step.status] || STATUS_CONFIG.SUCCESS

  return (
    <Box sx={{ display: 'flex', gap: 1.5, py: 1.5 }}>
      {/* Status icon */}
      <Box sx={{ pt: 0.25, flexShrink: 0 }}>
        {statusCfg.icon}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
            {step.title}
          </Typography>
          {step.duration_ms != null && (
            <Chip
              icon={<Timer sx={{ fontSize: '12px !important' }} />}
              label={step.duration_ms > 1000 ? `${(step.duration_ms / 1000).toFixed(1)}s` : `${step.duration_ms}ms`}
              size="small"
              sx={{ height: 20, fontSize: 10, bgcolor: '#F1F5F9', color: '#64748B' }}
            />
          )}
          {step.tool_name && (
            <Chip
              icon={<Code sx={{ fontSize: '12px !important' }} />}
              label={step.tool_name}
              size="small"
              sx={{ height: 20, fontSize: 10, bgcolor: '#EFF6FF', color: '#1D4ED8' }}
            />
          )}
        </Box>

        {step.description && (
          <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
            {step.description}
          </Typography>
        )}

        {step.findings && (
          <Box sx={{ mt: 1, p: 1.5, bgcolor: '#F0FDF4', borderRadius: 1.5, border: '1px solid #BBF7D0' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#166534', display: 'block', mb: 0.5 }}>
              Dados captados:
            </Typography>
            <Typography variant="caption" sx={{ color: '#15803D', whiteSpace: 'pre-wrap' }}>
              {step.findings}
            </Typography>
          </Box>
        )}

        {/* Tool input/output expandível */}
        {(step.tool_input || step.tool_output) && (
          <Box sx={{ mt: 1 }}>
            <Box
              onClick={() => setShowOutput(!showOutput)}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                cursor: 'pointer', color: '#64748B', '&:hover': { color: '#475569' },
              }}
            >
              {showOutput ? <KeyboardArrowUp sx={{ fontSize: 14 }} /> : <KeyboardArrowDown sx={{ fontSize: 14 }} />}
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {showOutput ? 'Ocultar detalhes técnicos' : 'Ver detalhes técnicos'}
              </Typography>
            </Box>

            <Collapse in={showOutput}>
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {step.tool_input && (
                  <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 1, border: '1px solid #E2E8F0' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', display: 'block', mb: 0.5 }}>
                      Parâmetros enviados:
                    </Typography>
                    <Box component="pre" sx={{ m: 0, fontSize: 11, color: '#334155', overflow: 'auto', maxHeight: 150, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {JSON.stringify(step.tool_input, null, 2)}
                    </Box>
                  </Box>
                )}
                {step.tool_output && (
                  <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 1, border: '1px solid #E2E8F0' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                        Resultado:
                      </Typography>
                      <Tooltip title="Copiar">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(step.tool_output) }}>
                          <ContentCopy sx={{ fontSize: 12 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box component="pre" sx={{ m: 0, fontSize: 11, color: '#334155', overflow: 'auto', maxHeight: 300, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {step.tool_output}
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </Box>
  )
}


const InvestigationSteps = ({ investigations, onDelete }) => {
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const handleDelete = (invId) => {
    setDeleting(true)
    api.delete(`tickets/investigations/${invId}/`)
      .then(() => {
        setConfirmDelete(null)
        onDelete?.(invId)
      })
      .catch(() => alert('Erro ao deletar investigação'))
      .finally(() => setDeleting(false))
  }

  if (!investigations || investigations.length === 0) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {investigations.map((inv) => {
        const totalSteps = inv.steps?.length || 0
        const successSteps = inv.steps?.filter(s => s.status === 'SUCCESS').length || 0
        const failedSteps = inv.steps?.filter(s => s.status === 'FAILED').length || 0

        return (
          <Card key={inv.id} sx={{ border: '1px solid #E2E8F0' }}>
            <Accordion
              defaultExpanded={investigations.indexOf(inv) === 0}
              sx={{
                boxShadow: 'none',
                '&:before': { display: 'none' },
                '& .MuiAccordionSummary-root': { minHeight: 48 },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" gap={1.5} width="100%">
                  <SmartToy sx={{ fontSize: 20, color: '#7C3AED' }} />
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Investigação IA
                      </Typography>
                      <Chip
                        label={inv.model_name || 'IA'}
                        size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: '#F3E8FF', color: '#7C3AED' }}
                      />
                      <Chip
                        label={inv.triggered_by === 'cron' ? 'Automática' : 'Manual'}
                        size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: inv.triggered_by === 'cron' ? '#DBEAFE' : '#FEF3C7', color: inv.triggered_by === 'cron' ? '#1D4ED8' : '#92400E' }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      {moment(inv.created_at).format('DD/MM/YYYY [às] HH:mm')}
                      {inv.duration_seconds && ` — ${inv.duration_seconds.toFixed(1)}s`}
                      {` — ${totalSteps} etapas`}
                      {failedSteps > 0 && ` (${failedSteps} falha${failedSteps > 1 ? 's' : ''})`}
                      {inv.total_tokens > 0 && ` — ${inv.total_tokens.toLocaleString('pt-BR')} tokens`}
                      {inv.input_tokens > 0 && ` (↑${inv.input_tokens.toLocaleString('pt-BR')} ↓${inv.output_tokens.toLocaleString('pt-BR')})`}
                    </Typography>
                  </Box>
                  <Chip
                    label={inv.status_display}
                    size="small"
                    sx={{
                      height: 22, fontWeight: 600, fontSize: 11, mr: 1,
                      bgcolor: inv.status === 'COMPLETED' ? '#D1FAE5' : inv.status === 'FAILED' ? '#FEE2E2' : '#FEF3C7',
                      color: inv.status === 'COMPLETED' ? '#065F46' : inv.status === 'FAILED' ? '#991B1B' : '#92400E',
                    }}
                  />
                  <Tooltip title="Excluir investigação" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setConfirmDelete(inv.id) }}
                      sx={{ ml: 0.5, color: '#94A3B8', '&:hover': { color: '#DC2626' } }}
                    >
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 0 }}>
                {/* Progress bar */}
                <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                  {inv.steps?.map((step) => {
                    const cfg = STATUS_CONFIG[step.status] || STATUS_CONFIG.SUCCESS
                    return (
                      <Tooltip key={step.id} title={`${step.title} — ${cfg.label}`} arrow>
                        <Box sx={{
                          flex: 1, height: 4, borderRadius: 2,
                          bgcolor: cfg.color,
                        }} />
                      </Tooltip>
                    )
                  })}
                </Box>

                {/* Steps */}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {inv.steps?.map((step, idx) => (
                    <React.Fragment key={step.id}>
                      <StepDetail step={step} />
                      {idx < inv.steps.length - 1 && (
                        <Divider sx={{ ml: 3.5 }} />
                      )}
                    </React.Fragment>
                  ))}
                </Box>

                {/* Error message */}
                {inv.error_message && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#FEE2E2', borderRadius: 1.5, border: '1px solid #FECACA' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#991B1B' }}>
                      Erro: {inv.error_message}
                    </Typography>
                  </Box>
                )}

                {/* Full AI Response */}
                {inv.full_response && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                      Relatório da IA
                    </Typography>
                    <Box sx={{
                      p: 2, bgcolor: '#FAFAFA', borderRadius: 2, border: '1px solid #E2E8F0',
                      '& h3': { fontSize: 15, fontWeight: 700, mt: 2, mb: 1, color: '#1E293B' },
                      '& h4': { fontSize: 14, fontWeight: 600, mt: 1.5, mb: 0.5, color: '#334155' },
                      '& p': { fontSize: 14, lineHeight: 1.6, mb: 1, color: '#334155' },
                      '& ul, & ol': { pl: 2.5, mb: 1 },
                      '& li': { fontSize: 14, lineHeight: 1.6, color: '#334155', mb: 0.5 },
                      '& code': { fontSize: 12, bgcolor: '#F1F5F9', px: 0.5, py: 0.25, borderRadius: 0.5, fontFamily: 'monospace' },
                      '& strong': { fontWeight: 600, color: '#1E293B' },
                      '& table': { width: '100%', borderCollapse: 'collapse', mb: 1.5, fontSize: 13 },
                      '& th, & td': { border: '1px solid #E2E8F0', p: 1, textAlign: 'left' },
                      '& th': { bgcolor: '#F8FAFC', fontWeight: 600 },
                    }}>
                      <Markdown remarkPlugins={[remarkBreaks]}>
                        {inv.full_response}
                      </Markdown>
                    </Box>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          </Card>
        )
      })}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir esta investigação? Os passos e dados serão removidos permanentemente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancelar</Button>
          <Button onClick={() => handleDelete(confirmDelete)} color="error" disabled={deleting} variant="contained">
            {deleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default InvestigationSteps
